import * as ko from 'knockout';
import fetch from 'node-fetch';
import { ReCaptcha } from 'grecaptcha';

import { Fido2Manager } from '../fido';
import { AuthAttribute, AuthMethod, NullAuthMethod, AuthRequest, AuthResponse, AuthState, UserMessage, UserMessageType } from '../contracts';
import template from './Auth.html';
import { ViewId, isAllowedUrl, isAllowedScriptUrl } from '../configuration';

class Auth {

  /// Diagnostics for #605. Not routed through global.Logonme.log, which is
  /// gated behind the tenant's enableDebugLogging flag. Remove or gate before
  /// this becomes the default bundle.
  private static diag(msg: string): void {
    // eslint-disable-next-line no-console
    console.log(`[605 ${new Date().toISOString().substr(11, 12)}] ${msg}`);
  }

  reCaptcha: ReCaptcha | null = null;
  pollTimerId: number | null = null;
  shouldPoll: boolean = false;
  /// The /forms/auth request currently on the wire, or null. See authenticate().
  private authInFlight: Promise<AuthResponse> | null = null;
  visibilityHandler: (() => void) | null = null;
  title: ko.Observable<string> = ko.observable<string>("");
  subTitle: ko.Observable<string> = ko.observable<string>("");
  link1Text: ko.Observable<string> = ko.observable<string>("");
  link1Uri: ko.Observable<string> = ko.observable<string>("");
  link2Text: ko.Observable<string> = ko.observable<string>("");
  link2Uri: ko.Observable<string> = ko.observable<string>("");
  link3Text: ko.Observable<string> = ko.observable<string>("");
  link3Uri: ko.Observable<string> = ko.observable<string>("");
  message: ko.Observable<string | null> = ko.observable<string | null>(null);
  messageType: ko.Observable<string> = ko.observable<string>("");
  infoTitle: ko.Observable<string> = ko.observable<string>("");
  infoDescription: ko.Observable<string> = ko.observable<string>("");
  infoLink1Text: ko.Observable<string> = ko.observable<string>("");
  infoLink1Uri: ko.Observable<string> = ko.observable<string>("");
  infoLink2Text: ko.Observable<string> = ko.observable<string>("");
  infoLink2Uri: ko.Observable<string> = ko.observable<string>("");

  backLink: ko.Observable<string> = ko.observable<string>("");
  cancelButton: ko.Observable<string> = ko.observable<string>("");
  submitButton: ko.Observable<string> = ko.observable<string>("");
  challengeAttributes: ko.ObservableArray<AuthAttribute> = ko.observableArray<AuthAttribute>([]);
  inProgress: ko.Observable<boolean> = ko.observable<boolean>(false);
  cancelRequested: ko.Observable<boolean> = ko.observable<boolean>(false);
  showMethodInfo: ko.Observable<boolean> = ko.observable<boolean>(false);

  // Computed properties - initialized in constructor
  hasMessage: ko.PureComputed<boolean>;
  selectedMethod: ko.PureComputed<AuthMethod>;
  selectedMethodAndPath: ko.PureComputed<string>;
  selectedLanguage: ko.PureComputed<string>;
  activateView: ko.PureComputed<boolean>;
  showCancelButton: ko.PureComputed<boolean>;
  showSubmitButton: ko.PureComputed<boolean>;

  constructor() {
    global.Logonme.log("Auth:constructor");

    // Initialize computed properties
    this.hasMessage = ko.pureComputed<boolean>(() => this.message() != null && this.message() !== '');
    this.selectedMethod = ko.pureComputed<AuthMethod>(() => global.Logonme.SelectedMethod() ?? new NullAuthMethod());
    this.selectedMethodAndPath = ko.pureComputed<string>(() => {
      const method = this.selectedMethod();
      return method.id + '@' + method.path;
    });
    this.selectedLanguage = ko.pureComputed<string>(() => global.Logonme.SelectedLanguage());
    this.activateView = ko.pureComputed<boolean>(() => global.Logonme.ActiveView() === ViewId.Auth);
    this.showCancelButton = ko.pureComputed<boolean>(() => this.inProgress());
    this.showSubmitButton = ko.pureComputed<boolean>(() => !this.inProgress());

    this.updateStrings(this.selectedLanguage(), this.selectedMethod());
    this.selectedLanguage.subscribe((lang: string) => {
      this.updateStrings(lang, this.selectedMethod());
    });

    this.visibilityHandler = () => {
      // Unchanged from v6.1.7 by design (#605): this unguarded resume is also
      // the recovery path for frozen timers and killed fetches. The double
      // request it used to cause is coalesced in authenticate() instead.
      Auth.diag(`visibilitychange hidden=${document.hidden} shouldPoll=${this.shouldPoll}`
        + ` cancelled=${this.cancelRequested()} inFlight=${this.authInFlight !== null}`);
      if (!document.hidden && this.shouldPoll && !this.cancelRequested()) {
        global.Logonme.log("Auth:visibilitychange - resuming poll");
        this.clearPollTimer();
        void this.callAuthenticate();
      }
    };
    document.addEventListener('visibilitychange', this.visibilityHandler);

    this.activateView.subscribe((activate: boolean) => {
      if (activate) {
        global.Logonme.log("Auth:activate");
        this.message(null);
        this.challengeAttributes([]);
        this.cancelRequested(false);
        this.shouldPoll = false;
        this.doAuthenticate();
      } else {
        this.shouldPoll = false;
      }
    });

    global.Logonme.ViewConstructedCount(global.Logonme.ViewConstructedCount() + 1);
  }

  private updateStrings(lang: string, method: AuthMethod): void {
    this.title(global.Logonme.LocalizationManager.getPropertyX(method, ['title'], null, lang));
    this.subTitle(global.Logonme.LocalizationManager.getPropertyX(method, ['subTitle'], null, lang));

    this.link1Text(global.Logonme.LocalizationManager.getPropertyX(method, ['links', 'link1', 'text'], null, lang));
    this.link1Uri(global.Logonme.LocalizationManager.getPropertyX(method, ['links', 'link1', 'uri'], null, lang));
    this.link2Text(global.Logonme.LocalizationManager.getPropertyX(method, ['links', 'link2', 'text'], null, lang));
    this.link2Uri(global.Logonme.LocalizationManager.getPropertyX(method, ['links', 'link2', 'uri'], null, lang));
    this.link3Text(global.Logonme.LocalizationManager.getPropertyX(method, ['links', 'link3', 'text'], null, lang));
    this.link3Uri(global.Logonme.LocalizationManager.getPropertyX(method, ['links', 'link3', 'uri'], null, lang));

    this.backLink(global.Logonme.LocalizationManager.getPropertyX(method, ['buttons', 'back'], null, lang));
    this.cancelButton(global.Logonme.LocalizationManager.getPropertyX(method, ['buttons', 'cancel'], null, lang));
    this.submitButton(global.Logonme.LocalizationManager.getPropertyX(method, ['buttons', 'submit'], null, lang));

    const infoTitle = global.Logonme.LocalizationManager.getPropertyX(method, ['info', 'title'], null, lang);
    const infoDescription = global.Logonme.LocalizationManager.getPropertyX(method, ['info', 'description'], null, lang);
    const showInfo = infoTitle || infoDescription;
    this.showMethodInfo(!!showInfo);

    this.infoTitle(infoTitle);
    this.infoDescription(infoDescription);
    this.infoLink1Text(global.Logonme.LocalizationManager.getPropertyX(method, ['info', 'link1', 'text'], null, lang));
    this.infoLink1Uri(global.Logonme.LocalizationManager.getPropertyX(method, ['info', 'link1', 'uri'], null, lang));
    this.infoLink2Text(global.Logonme.LocalizationManager.getPropertyX(method, ['info', 'link2', 'text'], null, lang));
    this.infoLink2Uri(global.Logonme.LocalizationManager.getPropertyX(method, ['info', 'link2', 'uri'], null, lang));
  }

  private updateAttributeAndMessageStrings(lang: string, method: AuthMethod, message: UserMessage): void {
    const messageId = message.message ?? "";
    const messageValue = global.Logonme.LocalizationManager.getMessage(method, messageId, null, lang);

    if (messageValue != null && messageValue !== '') {
      this.message(messageValue);
      switch (message.messageType) {
        case UserMessageType.Information:
          this.messageType("user-message-information");
          break;
        case UserMessageType.Warning:
          this.messageType("user-message-warning");
          break;
        case UserMessageType.Error:
          this.messageType("user-message-error");
          break;
        case UserMessageType.Success:
          this.messageType("user-message-success");
          break;
        default:
          break;
      }
    } else {
      this.message(null);
    }
  }

  /// #605: two concurrent /forms/auth requests collect the same BankID order;
  /// one gets 'no such order' and the server fails the whole login. This
  /// happens ~50% of same-device logins, because the visibilitychange resume
  /// (added in v6.1.7 for the iOS Safari timer freeze) fires while the poll
  /// request suspended with the page is still in flight.
  ///
  /// Fix: coalesce, don't suppress. A caller arriving while a request is on
  /// the wire JOINS the existing promise instead of opening a second HTTP
  /// request. Every trigger path still runs its full continuation from the
  /// shared settlement, so v6.1.7's liveness behaviour - including recovery
  /// via the unguarded resume - is preserved exactly. Nothing is declined or
  /// deferred, so no new hang is constructible; the worst case is by
  /// construction what v6.1.7 already does today.
  ///
  /// Cancel never joins: it must reach the server even mid-poll.
  private authenticate(request: AuthRequest): Promise<AuthResponse> {
    if (this.authInFlight !== null && !request.cancel) {
      Auth.diag("request already on the wire - joining it");
      return this.authInFlight;
    }

    const p: Promise<AuthResponse> = this.authenticateWire(request).finally(() => {
      if (this.authInFlight === p) this.authInFlight = null;
    });
    if (!request.cancel) this.authInFlight = p;
    return p;
  }

  private async authenticateWire(request: AuthRequest): Promise<AuthResponse> {
    const uri = `${global.Logonme.Configuration.baseUrl}/ls/${global.Logonme.Configuration.tenant}/forms/auth`;
    const startedAt = Date.now();
    Auth.diag(`request start${request.cancel ? " (cancel)" : ""}`);
    try {
      const response = await fetch(uri, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(request)
      });

      const body = await response.json() as AuthResponse;
      Auth.diag(`request done in ${Date.now() - startedAt}ms authState=${body.authState}`);
      return body;
    } catch (e) {
      // On Chrome/iOS the fetch is killed when the BankID app takes over, so
      // this path is routine there. Behaviour on rejection is deliberately
      // unchanged from v6.1.7: the settled state clears, and the next resume
      // or timer restarts the loop exactly as it always has.
      Auth.diag(`request FAILED after ${Date.now() - startedAt}ms: ${e}`);
      throw e;
    }
  }

  private clearPollTimer(): void {
    this.shouldPoll = false;
    if (this.pollTimerId !== null) {
      clearTimeout(this.pollTimerId);
      this.pollTimerId = null;
    }
  }

  async doBack(): Promise<void> {
    this.clearPollTimer();
    this.cancelRequested(true);
    // Immediately trigger a cancel request to the server
    await this.callAuthenticate();
  }

  async doCancel(): Promise<void> {
    this.clearPollTimer();
    this.cancelRequested(true);
    // Immediately trigger a cancel request to the server
    await this.callAuthenticate();
  }

  async doAuthenticate(): Promise<void> {
    this.inProgress(true);
    await this.callAuthenticate();
    this.inProgress(false);
  }

  private convertAttributes(method: AuthMethod, challengeAttributes: AuthAttribute[]): AuthAttribute[] {
    const shouldHaveFocus = (m: AuthMethod, attribute: AuthAttribute): boolean => {
      if (attribute.persisted) {
        return false;
      }
      const aid = attribute.name ?? "";
      const attrType = global.Logonme.LocalizationManager.getPropertyX(m, ['attributes', aid, 'inputType'], null, null);
      return attrType === "text" || attrType === "password";
    };

    const compareSortOrder = (m: AuthMethod, attributeA: AuthAttribute, attributeB: AuthAttribute): number => {
      const aidA = attributeA.name ?? "";
      const sortOrderA = Number.parseInt(global.Logonme.LocalizationManager.getPropertyX(m, ['attributes', aidA, 'sortOrder'], "0", null));
      const aidB = attributeB.name ?? "";
      const sortOrderB = Number.parseInt(global.Logonme.LocalizationManager.getPropertyX(m, ['attributes', aidB, 'sortOrder'], "0", null));
      return sortOrderA < sortOrderB ? -1 : 1;
    };

    const sorted = challengeAttributes.sort((a, b) => compareSortOrder(method, a, b));
    const focusAttribute = sorted.find((attr: AuthAttribute) => shouldHaveFocus(method, attr));
    if (focusAttribute) {
      focusAttribute.hasFocus = true;
    }

    return sorted;
  }

  private async callAuthenticate(): Promise<void> {
    global.Logonme.log("callAuthenticate");

    const method = this.selectedMethod();
    const lang = this.selectedLanguage();
    const route = this.selectedMethodAndPath();
    if (route == null || route === 'null@null') {
      throw new Error('Route not set');
    }

    const request = new AuthRequest();
    request.challengeAttributes = this.challengeAttributes();
    request.selectedMethodAndPath = route;
    request.cancel = this.cancelRequested();
    request.startupParams = global.Logonme.Configuration.startupParams;

    const response = await this.authenticate(request);
    global.Logonme.log(response);

    const sorted = this.convertAttributes(method, response.challengeAttributes ?? []);
    this.challengeAttributes(sorted);
    global.Logonme.AuthState(response.authState);

    // When a step completes (PathPartialComplete), navigate back to method list
    // to fetch the next step. We must explicitly clear SelectedMethod because
    // AuthState may already be PathPartialComplete from a previous step,
    // and Knockout won't fire the subscription for the same value.
    if (response.authState === AuthState.PathPartialComplete) {
      global.Logonme.SelectedMethod(null);
      return;
    }

    this.updateStrings(lang, method);
    if (response.message) {
      this.updateAttributeAndMessageStrings(lang, method, response.message);
    }

    const activeReCaptcha = await this.activeReCaptcha(response);
    if (activeReCaptcha) {
      global.Logonme.log("re-captcha activated");
    }

    const autoPostResult = await this.handleFidoCommands(response);
    if (autoPostResult) {
      global.Logonme.log("auto-posting result");
      await this.callAuthenticate();
    }

    this.inProgress(response.pollServer);
    this.shouldPoll = response.pollServer;

    if (response.pollServer && !this.cancelRequested()) {
      this.pollTimerId = window.setTimeout(() => {
        this.pollTimerId = null;
        if (!this.cancelRequested() && this.shouldPoll) {
          global.Logonme.log("polling server");
          void this.callAuthenticate();
        }
      }, 1000);
    }

    if (response.launchExternal && response.launchUri) {
      const allowedTargets = global.Logonme.FrontendConfig.allowedRedirectTargets;
      if (!isAllowedUrl(response.launchUri, allowedTargets)) {
        const msg = `Redirect blocked: '${response.launchUri}' is not in the allowed redirect targets. Update 'Allowed Redirect Targets' in Authentication Service Settings.`;
        console.error(msg);
        this.message(msg);
        this.messageType("user-message-error");
        return;
      }
      global.Logonme.LastLoginStore.remove();
      global.Logonme.log(`auth: re-directing to uri ${response.launchUri}`);
      window.location.href = response.launchUri;
    }
  }

  private async handleFidoCommands(response: AuthResponse): Promise<boolean> {
    if (response.challengeAttributes == null || response.authState !== AuthState.PathInProgress) {
      return false;
    }

    try {
      const fido2 = new Fido2Manager();
      const attrAuthChallenge = response.challengeAttributes.find(a => a.name === AuthRequest.AttributeAuthChallenge);
      const attrAuthResponse = response.challengeAttributes.find(a => a.name === AuthRequest.AttributeAuthResponse);
      if (attrAuthChallenge != null && attrAuthResponse != null) {
        const fidoChallenge = attrAuthChallenge.value;
        const fidoResponse: string = await fido2.handleFidoLogin(attrAuthChallenge.value);
        global.Logonme.log(`FIDO2 Authentication: ${fidoChallenge} => ${fidoResponse}`);
        attrAuthResponse.value = fidoResponse;
        return true;
      }

      const attrRegChallenge = response.challengeAttributes.find(a => a.name === AuthRequest.AttributeRegistrationChallenge);
      const attrRegResponse = response.challengeAttributes.find(a => a.name === AuthRequest.AttributeRegistrationResponse);
      if (attrRegChallenge != null && attrRegResponse != null) {
        const regChallenge = attrRegChallenge.value;
        const regResponse: string = await fido2.handleFidoAttestation(regChallenge);
        global.Logonme.log(`FIDO2 Registration: ${regChallenge} => ${regResponse}`);
        attrRegResponse.value = regResponse;
        return true;
      }

      return false;
    } catch (exception: unknown) {
      alert(`${exception}`);
      return false;
    }
  }

  private loadScriptAsync(url: string): Promise<Event> {
    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = url;
      script.async = true;
      script.onload = resolve;
      script.onerror = reject;
      document.body.appendChild(script);
    });
  }

  private async activeReCaptcha(response: AuthResponse): Promise<boolean> {
    if (response.challengeAttributes == null || response.authState !== AuthState.PathInProgress) {
      return false;
    }

    const siteKey = response.challengeAttributes.find(a => a.name === AuthRequest.AttributeReCaptchaSiteKey)?.value;
    if (siteKey == null) {
      return false;
    }

    if (this.reCaptcha == null) {
      const scriptUrl = response.challengeAttributes.find(a => a.name === AuthRequest.AttributeReCaptchaScriptUrl)?.value ?? null;
      if (scriptUrl == null) {
        return false;
      }

      const allowedScripts = global.Logonme.FrontendConfig.allowedScriptSources;
      if (!isAllowedScriptUrl(scriptUrl, allowedScripts)) {
        const msg = `Script blocked: '${scriptUrl}' is not in the allowed script sources. Check CSP script-src configuration.`;
        console.error(msg);
        this.message(msg);
        this.messageType("user-message-error");
        return false;
      }
      await this.loadScriptAsync(scriptUrl);
      this.reCaptcha = await this.waitForReCaptchaIsReady();
      global.Logonme.log(`Re-captcha successfully loaded from ${scriptUrl}`);
    }

    const attrReCaptchaResponse = response.challengeAttributes.find(a => a.name === AuthRequest.AttributeReCaptchaResponse);
    if (attrReCaptchaResponse == null) {
      return false;
    }

    const action = response.challengeAttributes.find(a => a.name === AuthRequest.AttributeReCaptchaAction)?.value;
    if (action == null) {
      return false;
    }

    attrReCaptchaResponse.value = await this.reCaptcha?.execute(siteKey, { action: action });
    return true;
  }

  private waitForReCaptchaIsReady(): Promise<ReCaptcha> {
    return new Promise((resolve) => {
      grecaptcha.enterprise.ready(() => {
        const reCaptcha: ReCaptcha = grecaptcha.enterprise;
        resolve(reCaptcha);
      });
    });
  }
}



export default { viewModel: Auth, template: template };
